<template>
  <div class="ui-menu" ref="rootRef">
    <div class="ui-menu__trigger" @click="toggle">
      <slot name="trigger" />
    </div>
    <Teleport to="body">
      <div v-if="open" class="ui-menu__backdrop" @click="close(false)" aria-hidden="true" />
      <Transition name="ui-menu-drop">
        <div
          v-if="open"
          ref="panelRef"
          class="ui-menu__panel"
          :style="panelStyle"
          role="menu"
          @click.stop
          @keydown="onPanelKeydown"
        >
          <slot />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, onMounted, onUnmounted } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Позиция выпадающего меню */
    align?: 'left' | 'right'
  }>(),
  { align: 'left' },
)

const open = ref(false)
const rootRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
let opener: HTMLElement | null = null
const panelStyle = ref<{ top: string; left?: string; right?: string }>({ top: '0' })

function updatePosition() {
  if (!rootRef.value) return
  const r = rootRef.value.getBoundingClientRect()
  const top = r.bottom + 4
  panelStyle.value =
    props.align === 'right'
      ? { top: `${top}px`, right: `${window.innerWidth - r.right}px` }
      : { top: `${top}px`, left: `${r.left}px` }
}

function menuItems(): HTMLElement[] {
  return panelRef.value
    ? [...panelRef.value.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]')]
    : []
}

function toggle() {
  open.value = !open.value
  if (open.value) {
    const active = document.activeElement
    opener = active instanceof HTMLElement ? active : null
    requestAnimationFrame(updatePosition)
    // Клавиатурный пользователь должен попасть в меню, а не остаться на кнопке-триггере
    void nextTick(() => {
      const items = menuItems()
      items.forEach((item) => item.setAttribute('role', 'menuitem'))
      items[0]?.focus()
    })
  }
}

function close(restoreFocus = true) {
  open.value = false
  if (restoreFocus && opener?.isConnected) opener.focus()
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) close(true)
}

function onPanelKeydown(e: KeyboardEvent) {
  const items = menuItems()
  if (items.length === 0) return
  const index = items.indexOf(document.activeElement as HTMLElement)

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    items[(index + 1) % items.length]?.focus()
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    items[(index - 1 + items.length) % items.length]?.focus()
  } else if (e.key === 'Home') {
    e.preventDefault()
    items[0]?.focus()
  } else if (e.key === 'End') {
    e.preventDefault()
    items[items.length - 1]?.focus()
  } else if (e.key === 'Tab') {
    // Меню не ловушка: Tab закрывает его, фокус возвращается к триггеру и идёт дальше по странице
    close(true)
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
})

defineExpose({ open, close, toggle })
</script>

<style scoped>
.ui-menu {
  position: relative;
  display: inline-block;
}

.ui-menu__trigger {
  cursor: pointer;
}

.ui-menu__panel {
  position: fixed;
  z-index: 1001;
  min-width: 160px;
  padding: 4px 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.ui-menu__backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
}

.ui-menu-drop-enter-active,
.ui-menu-drop-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.ui-menu-drop-enter-from,
.ui-menu-drop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
